// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import type { WorkOrderPaginated } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { GridMenu } from 'components/Mantine/GridMenu';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { SELECTED_WORK_ORDER_QUERY_PARAM_KEY } from 'modules/Shop/EvolveFabrication/FabricationWorkOrders';
import { ReassignMachineProfiles } from 'modules/Shop/EvolveFabrication/ReassignMachineProfiles';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import { useWorkOrderModals } from 'modules/Shop/WorkOrders/WorkOrdersPage/modalRegistries';

import { useWorkOrders } from './useWorkOrders';

type Props = {
  workOrder: WorkOrderPaginated;
  onWorkOrderActioned: () => void;
  onWorkOrderDeleted: () => void;
  loading?: boolean;
  isWorkOrdersPage?: boolean;
};

export const ActionWorkOrderMenu = ({
  workOrder,
  onWorkOrderActioned,
  onWorkOrderDeleted,
  loading,
  isWorkOrdersPage,
}: Props) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const hasFabricationFullAccess = useHasAccess('fabrication', 'write');
  const navigate = useEvolveNavigate('Work Orders');
  const { mutationRunning } = useWorkOrders();
  const { openModal } = useWorkOrderModals();

  return (
    <GridMenu
      loading={loading}
      options={[
        {
          value: 'Copy link to work order',
          hidden: isWorkOrdersPage,
        },
        {
          value: 'Reassign machine profiles',
          hidden: isWorkOrdersPage || !hasFabricationFullAccess,
        },
        {
          value: 'View details',
          hidden: !isWorkOrdersPage,
        },
        {
          label: 'Print work order traveler',
          value: 'traveler',
        },
        {
          value: 'Block',
          hidden: !canEditWorkOrder('block'),
          color: 'red',
        },
        {
          value: 'Reject',
          hidden: !canEditWorkOrder('reject'),
          color: 'red',
        },
        {
          value: 'Delete',
          hidden: !canEditWorkOrder('delete'),
          color: 'red',
        },
      ]}
      disabled={mutationRunning}
      onMenuItemSelected={(item) => {
        if (item === 'View details') {
          void navigate(`/work-orders/${workOrder.workOrderId}`);
          return true;
        }
        if (item === 'Copy link to work order') {
          void navigator.clipboard
            .writeText(
              `${window.location.origin}${window.location.pathname}?${SELECTED_WORK_ORDER_QUERY_PARAM_KEY}=${workOrder.workOrderId}`,
            )
            .then(() => {
              notifications.show({
                title: 'Work order link copied',
                message: (
                  <>
                    A link to work order <b>{workOrder.name}</b> has been copied to your clipboard.
                  </>
                ),
                color: 'green',
              });
            })
            .catch(() => {
              notifications.show({
                title: 'Error copying link',
                message: "The link to the work order was not copied. Check your browser's clipboard permissions.",
                color: 'red',
              });
            });
          return true;
        }
        if (item === 'traveler') {
          openModal('workOrderTravelerModal', { workOrder });
          return true;
        }
        if (item === 'Block') {
          openModal('blockWorkOrderModal', { workOrder, onWorkOrderActioned });
          return true;
        }
        if (item === 'Reject') {
          openModal('rejectWorkOrderModal', { workOrder, onWorkOrderActioned });
          return true;
        }
        if (item === 'Delete') {
          openModal('deleteWorkOrderModal', { workOrder, onWorkOrderActioned: onWorkOrderDeleted });
          return true;
        }
        return false;
      }}
    >
      {(menuItemSelected, resetMenu) => (
        <ReassignMachineProfiles
          opened={menuItemSelected === 'Reassign machine profiles'}
          onClose={resetMenu}
          workOrder={workOrder}
        />
      )}
    </GridMenu>
  );
};
